import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Package, CreditCard, EyeOff, ChevronUp, ChevronDown, GripVertical } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { API } from "../../App";

const AdminPacks = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ 
    name: "", 
    service_type: "grupo", 
    category: "pack",
    credits: 1, 
    price: 25, 
    description: "", 
    popular: false, 
    sort_order: 0,
    weekly_credits: null,
    visible: true,
  });

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/services`);
      setServices(res.data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    } catch (err) { 
      console.error("Error fetching services:", err);
      toast.error("Erro ao carregar serviços"); 
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        weekly_credits: form.category === "mensalidade" ? (form.weekly_credits || 2) : null
      };
      if (editingService) {
        await axios.put(`${API}/admin/packs/${editingService}`, payload, { withCredentials: true });
        toast.success("Serviço atualizado!");
      } else {
        await axios.post(`${API}/admin/packs`, payload, { withCredentials: true });
        toast.success("Serviço criado!");
      }
      setShowForm(false);
      setEditingService(null);
      resetForm();
      fetchServices();
    } catch { toast.error("Erro ao guardar serviço"); }
  };

  const resetForm = () => {
    setForm({ name: "", service_type: "grupo", category: "pack", credits: 1, price: 25, description: "", popular: false, sort_order: 0, weekly_credits: null, visible: true });
  };

  const handleEdit = (service) => {
    setForm({ 
      name: service.name, 
      service_type: service.service_type || (service.pack_type === "private" ? "privado" : "grupo"), 
      category: service.category || "pack",
      credits: service.credits, 
      price: service.price, 
      description: service.description || "", 
      popular: service.popular || false, 
      sort_order: service.sort_order || 0,
      weekly_credits: service.weekly_credits,
      visible: service.visible !== false,
    });
    setEditingService(service.pack_id);
    setShowForm(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm("Desativar este serviço?")) return;
    try {
      await axios.delete(`${API}/admin/packs/${serviceId}`, { withCredentials: true });
      toast.success("Serviço desativado");
      fetchServices();
    } catch { toast.error("Erro ao desativar serviço"); }
  };

  const handleMoveOrder = async (service, direction) => {
    const currentList = service.category === "mensalidade" ? mensalidades : packs;
    const currentIndex = currentList.findIndex(s => s.pack_id === service.pack_id);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= currentList.length) return;
    
    const otherService = currentList[newIndex];
    
    // Use index-based ordering to ensure unique values after swap
    // The item moving "up" gets the smaller index, the one moving "down" gets the larger
    const smallerOrder = Math.min(currentIndex, newIndex);
    const largerOrder = Math.max(currentIndex, newIndex);
    
    try {
      // Assign new sort_order values based on their new positions
      await Promise.all([
        axios.put(`${API}/admin/packs/${service.pack_id}`, { ...service, sort_order: newIndex }, { withCredentials: true }),
        axios.put(`${API}/admin/packs/${otherService.pack_id}`, { ...otherService, sort_order: currentIndex }, { withCredentials: true })
      ]);
      fetchServices();
    } catch { toast.error("Erro ao reordenar"); }
  };

  // Separate services by category
  const packs = services.filter(s => s.category !== "mensalidade");
  const mensalidades = services.filter(s => s.category === "mensalidade");

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#3d5a63] mb-2">Serviços & Preços</h1>
          <p className="text-[#475569]">Gerir aulas, packs e mensalidades</p>
        </div>
        <Button onClick={() => { setEditingService(null); resetForm(); setShowForm(true); }} className="bg-[#6697a4] hover:bg-[#3d5a63]" data-testid="add-service-btn">
          <Plus className="h-4 w-4 mr-2" /> Novo Serviço
        </Button>
      </div>

      {showForm && (
        <Card className="bg-white border-0 shadow-sm rounded-xl">
          <CardContent className="p-6">
            <h3 className="font-semibold text-[#3d5a63] mb-4">{editingService ? "Editar Serviço" : "Novo Serviço"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-[#475569] block mb-1">Nome</label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Ex: Pack 5 Aulas" data-testid="service-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#475569] block mb-1">Categoria</label>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="service-category-select">
                  <option value="pack">Pack / Aula Avulsa</option>
                  <option value="mensalidade">Mensalidade (Subscrição)</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#475569] block mb-1">Tipo de Aula</label>
                <select value={form.service_type} onChange={(e) => setForm({...form, service_type: e.target.value})} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="service-type-select">
                  <option value="grupo">Grupo</option>
                  <option value="privado">Privado</option>
                </select>
              </div>
              {form.category !== "mensalidade" && (
                <div>
                  <label className="text-sm font-medium text-[#475569] block mb-1">Créditos (nº aulas)</label>
                  <Input type="number" min="1" value={form.credits} onChange={(e) => setForm({...form, credits: e.target.value === '' ? '' : parseInt(e.target.value)})} onBlur={(e) => setForm({...form, credits: parseInt(e.target.value) || 1})} data-testid="service-credits-input" />
                </div>
              )}
              {form.category === "mensalidade" && (
                <div>
                  <label className="text-sm font-medium text-[#475569] block mb-1">Créditos por Semana</label>
                  <Input type="number" min="1" value={form.weekly_credits || 2} onChange={(e) => setForm({...form, weekly_credits: e.target.value === '' ? '' : parseInt(e.target.value)})} onBlur={(e) => setForm({...form, weekly_credits: parseInt(e.target.value) || 2})} data-testid="service-weekly-credits-input" />
                  <p className="text-xs text-[#94A3B8] mt-1">Total mensal: {(form.weekly_credits || 2) * 4} créditos</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-[#475569] block mb-1">Preço (€)</label>
                <Input type="number" min="0" step="0.5" value={form.price} onChange={(e) => setForm({...form, price: e.target.value === '' ? '' : parseFloat(e.target.value)})} onBlur={(e) => setForm({...form, price: parseFloat(e.target.value) || 0})} data-testid="service-price-input" />
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <label className="text-sm font-medium text-[#475569] block mb-1">Descrição</label>
                <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Descrição breve do serviço" data-testid="service-desc-input" />
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.popular} onChange={(e) => setForm({...form, popular: e.target.checked})} className="rounded" />
                  Popular (destaque)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!form.visible} onChange={(e) => setForm({...form, visible: !e.target.checked})} className="rounded" data-testid="service-hidden-toggle" />
                  <EyeOff className="h-3.5 w-3.5 text-[#94A3B8]" /> Secreto (só admin)
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm">Ordem:</label>
                  <Input type="number" className="w-20" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: e.target.value === '' ? '' : parseInt(e.target.value)})} onBlur={(e) => setForm({...form, sort_order: parseInt(e.target.value) || 0})} />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSave} className="bg-[#6697a4] hover:bg-[#3d5a63]" data-testid="save-service-btn">
                {editingService ? "Guardar" : "Criar Serviço"}
              </Button>
              <Button variant="outline" onClick={() => { setShowForm(false); setEditingService(null); }}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#6697a4] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Mensalidades Section */}
          {mensalidades.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="h-5 w-5 text-[#6697a4]" />
                <h2 className="text-xl font-semibold text-[#3d5a63]">Mensalidades</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mensalidades.map((service, idx) => (
                  <Card key={service.pack_id} className="bg-gradient-to-r from-[#6697a4] to-[#3d5a63] text-white border-0 shadow-sm rounded-xl" data-testid={`admin-service-${service.pack_id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-2">
                          {/* Order controls */}
                          <div className="flex flex-col gap-0.5 -ml-1">
                            <button 
                              onClick={() => handleMoveOrder(service, "up")} 
                              disabled={idx === 0}
                              className={`p-0.5 rounded hover:bg-white/20 transition-colors ${idx === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                              title="Mover para cima"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleMoveOrder(service, "down")} 
                              disabled={idx === mensalidades.length - 1}
                              className={`p-0.5 rounded hover:bg-white/20 transition-colors ${idx === mensalidades.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                              title="Mover para baixo"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{service.name}</h3>
                              <Badge className="bg-white/20 text-white border-0 text-[10px]">Subscrição</Badge>
                              {service.visible === false && <Badge className="bg-yellow-500/80 text-white border-0 text-[10px]"><EyeOff className="h-2.5 w-2.5 mr-0.5" />Secreto</Badge>}
                            </div>
                            <p className="text-xs text-white/70">{service.weekly_credits || 2} créditos/semana</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">{service.price}€</p>
                          <p className="text-xs text-white/70">/mês</p>
                        </div>
                      </div>
                      <p className="text-sm text-white/80 mb-3">{service.description}</p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(service)} className="border-white/30 text-white hover:bg-white/20" data-testid={`edit-service-${service.pack_id}`}>
                          <Edit className="h-3 w-3 mr-1" /> Editar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(service.pack_id)} className="border-white/30 text-white hover:bg-red-500/30" data-testid={`delete-service-${service.pack_id}`}>
                          <Trash2 className="h-3 w-3 mr-1" /> Desativar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Packs Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Package className="h-5 w-5 text-[#6697a4]" />
              <h2 className="text-xl font-semibold text-[#3d5a63]">Packs & Aulas</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {packs.map((service, idx) => (
                <Card key={service.pack_id} className={`bg-white border-0 shadow-sm rounded-xl ${service.popular ? 'ring-2 ring-[#e8927c]' : ''}`} data-testid={`admin-service-${service.pack_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-2">
                        {/* Order controls */}
                        <div className="flex flex-col gap-0.5 -ml-1">
                          <button 
                            onClick={() => handleMoveOrder(service, "up")} 
                            disabled={idx === 0}
                            className={`p-0.5 rounded hover:bg-[#6697a4]/10 transition-colors ${idx === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                            title="Mover para cima"
                          >
                            <ChevronUp className="h-4 w-4 text-[#6697a4]" />
                          </button>
                          <button 
                            onClick={() => handleMoveOrder(service, "down")} 
                            disabled={idx === packs.length - 1}
                            className={`p-0.5 rounded hover:bg-[#6697a4]/10 transition-colors ${idx === packs.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                            title="Mover para baixo"
                          >
                            <ChevronDown className="h-4 w-4 text-[#6697a4]" />
                          </button>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-[#3d5a63]">{service.name}</h3>
                            {service.popular && <Badge className="bg-[#e8927c] text-white border-0 text-[10px]">Popular</Badge>}
                            {service.visible === false && <Badge className="bg-yellow-500 text-white border-0 text-[10px]"><EyeOff className="h-2.5 w-2.5 mr-0.5" />Secreto</Badge>}
                          </div>
                          <div className="flex gap-1">
                            <Badge variant="outline" className="text-xs">{service.service_type === "privado" ? "Privado" : "Grupo"}</Badge>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[#3d5a63]">{service.price}€</p>
                        <p className="text-xs text-[#94A3B8]">{service.credits} crédito{service.credits > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#94A3B8] mb-3">{service.description}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(service)} data-testid={`edit-service-${service.pack_id}`}>
                        <Edit className="h-3 w-3 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(service.pack_id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-service-${service.pack_id}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Desativar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPacks;
